import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import {
  createProductStoreCategoryAction,
  updateProductStoreCategoryAction,
} from "../slices/productStoreCategoriesSlice";
import { ProductStoreCategory } from "../api/fetchProductStoreCategories/types";
import { CreateProductStoreCategoryRequest } from "../api/createProductStoreCategory/types";
import { fetchProductStoreCategoryMetadata } from "../api/fetchProductStoreCategoryMetadata/fetchProductStoreCategoryMetadataApi";
import { ColorOption } from "../api/fetchProductStoreCategoryMetadata/types";

interface UseCategoryDialogParams {
  isOpen: boolean;
  onClose: () => void;
  category?: ProductStoreCategory | null;
  onSuccess?: () => void;
}

export function useCategoryDialog({
  isOpen,
  onClose,
  category,
  onSuccess,
}: UseCategoryDialogParams) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableColors, setAvailableColors] = useState<ColorOption[]>([]);

  const isEditing = !!category;

  useEffect(() => {
    if (isOpen) {
      fetchProductStoreCategoryMetadata()
        .then((response) => {
          setAvailableColors(response.data.colors);
        })
        .catch(() => {
          setAvailableColors([]);
        });
    }
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (data: CreateProductStoreCategoryRequest) => {
      setIsSubmitting(true);
      try {
        if (isEditing && category) {
          await dispatch(updateProductStoreCategoryAction(category.id, data));
          toast({
            title: "Success",
            description: "Category updated successfully",
            variant: "default",
          });
        } else {
          await dispatch(createProductStoreCategoryAction(data));
          toast({
            title: "Success",
            description: "Category created successfully",
            variant: "default",
          });
        }
        onClose();

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error ? error.message : "An error occurred",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, isEditing, category, onClose, onSuccess, toast],
  );

  return {
    isSubmitting,
    availableColors,
    isEditing,
    handleSubmit,
  };
}
